import { useEffect, useRef, useCallback, useState } from "react";
import { FaceMesh } from "@mediapipe/face_mesh";

// Landmark indices for iris centres (requires refineLandmarks: true)
const LEFT_IRIS_CENTER  = 468;
const RIGHT_IRIS_CENTER = 473;

const DETECT_INTERVAL_MS = 500; // 2 fps

const GRACE_FACE_MISSING    = 2000;
const GRACE_ATTENTION_DRIFT = 1500;
const GRACE_LOOKING_DOWN    = 2500;
const GRACE_LOOKING_AWAY    = 1500;

const COOLDOWN = {
  face_missing:     5000,
  multiple_faces:   8000,
  attention_drift:  5000,
  looking_down:     8000,
  looking_away:     8000,
};

// Head-pose thresholds — ratio-based, scale-invariant, range [-1, 1].
// Yaw:   (rightHalf - leftHalf) / totalWidth  — 0 = straight, ±1 = fully sideways
// Pitch: (noseToChin - noseToForehead) / totalHeight — 0 = straight, ±1 = fully up/down
const YAW_LIMIT        = 0.35;  // ~40° sideways turn
const PITCH_UP_LIMIT   = 0.22;  // looking_away: upward tilt (pitch < -PITCH_UP_LIMIT)
const PITCH_DOWN_LIMIT = 0.28;  // looking_down: chin-to-chest (pitch < -PITCH_DOWN_LIMIT)
                                 // Note: pitch goes MORE NEGATIVE when looking down (chin rises in 2D projection)
const IRIS_DOWN_RATIO  = 0.58;  // iris y / eye-height — max observed ~0.55; 0.58 requires clearly below-centre gaze

export default function useMediaPipeProctoring(videoRef, { enabled = false, onEvent } = {}) {
  const [cheatCount, setCheatCount]     = useState(0);
  const [faceDetected, setFaceDetected] = useState(false);

  const solutionRef  = useRef(null);   // FaceMesh instance
  const timerRef     = useRef(null);
  const eventsRef    = useRef([]);
  const gracesRef    = useRef({});
  const cooldownRef  = useRef({});
  const loadingRef   = useRef(false);
  const mountedRef   = useRef(true);

  // Latest results written synchronously by onResults, read after each send()
  const lastResultsRef = useRef(null);

  // Re-assert true on every mount so StrictMode's unmount→remount cycle doesn't
  // permanently kill the detection loop (cleanup sets it false, remount must reset it).
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const logEvent = useCallback((type) => {
    const now = Date.now();
    if (now - (cooldownRef.current[type] || 0) < COOLDOWN[type]) return;
    cooldownRef.current[type] = now;
    const evt = { type, at: new Date().toISOString(), duration_sec: 0 };
    eventsRef.current = [...eventsRef.current, evt];
    setCheatCount(eventsRef.current.length);
    onEvent?.(evt);
  }, [onEvent]);

  const checkGrace = useCallback((key, graceMs, shouldFire) => {
    if (!shouldFire) { gracesRef.current[key] = null; return; }
    const start = gracesRef.current[key];
    if (!start) {
      gracesRef.current[key] = Date.now();
    } else if (Date.now() - start >= graceMs) {
      gracesRef.current[key] = null;
      logEvent(key);
    }
  }, [logEvent]);

  const loadDetector = useCallback(async () => {
    if (solutionRef.current || loadingRef.current) return;
    loadingRef.current = true;
    try {
      const localPath = `${window.location.origin}${process.env.PUBLIC_URL || ""}/mediapipe`;
      const faceMesh = new FaceMesh({
        locateFile: (file) => `${localPath}/${file}`,
      });
      faceMesh.setOptions({
        maxNumFaces: 2,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      // onResults fires synchronously within send() — store results in a ref
      faceMesh.onResults((results) => {
        lastResultsRef.current = results;
      });
      await faceMesh.initialize();
      if (!mountedRef.current) { faceMesh.close(); return; }
      solutionRef.current = faceMesh;
      console.log("[proctoring] MediaPipe FaceMesh loaded from", localPath);
    } catch (err) {
      console.warn("[proctoring] FaceMesh failed to load:", err?.message || err);
    } finally {
      loadingRef.current = false;
    }
  }, []);

  const runDetection = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || !solutionRef.current) return;

    lastResultsRef.current = null;
    try {
      await solutionRef.current.send({ image: video });
    } catch (err) {
      console.warn("[proctoring] send() error:", err?.message || err);
      return;
    }

    if (!mountedRef.current) return;

    const results = lastResultsRef.current;
    if (!results) return; // onResults didn't fire — skip cycle

    const faces = results.multiFaceLandmarks || [];
    const vw = video.videoWidth  || 640;
    const vh = video.videoHeight || 480;

    setFaceDetected(faces.length === 1);

    // ── Multiple faces ───────────────────────────────────────────────────────
    if (faces.length > 1) {
      logEvent("multiple_faces");
      return;
    }

    // ── Face missing ─────────────────────────────────────────────────────────
    checkGrace("face_missing", GRACE_FACE_MISSING, faces.length === 0);
    if (faces.length === 0) return;

    // landmarks are normalised [0,1] — scale to pixels for geometry
    const kpRaw = faces[0];
    const kp = kpRaw.map(p => ({ x: p.x * vw, y: p.y * vh, z: p.z * vw }));

    // ── Attention drift (face centroid outside centre zone) ──────────────────
    const xs = kp.map(p => p.x), ys = kp.map(p => p.y);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2 / vw;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2 / vh;
    checkGrace("attention_drift", GRACE_ATTENTION_DRIFT,
      cx < 0.2 || cx > 0.8 || cy < 0.15 || cy > 0.85);

    // ── Head pose — ratio-based, scale-invariant ────────────────────────────
    // Yaw:   compare left/right face half-widths (landmarks 234=left cheek, 454=right cheek)
    // Pitch: compare nose-to-forehead vs nose-to-chin distances
    const nose      = kp[1];
    const forehead  = kp[10];
    const chin      = kp[152];
    const leftEdge  = kp[234];  // left cheek/temple
    const rightEdge = kp[454];  // right cheek/temple

    if (nose && forehead && chin && leftEdge && rightEdge) {
      const leftHalf  = nose.x - leftEdge.x;
      const rightHalf = rightEdge.x - nose.x;
      const yawTotal  = leftHalf + rightHalf;
      const yaw = yawTotal > 1 ? (rightHalf - leftHalf) / yawTotal : 0;
      // positive = turning left, negative = turning right

      const noseToForehead = nose.y - forehead.y;
      const noseToChin     = chin.y  - nose.y;
      const pitchTotal     = noseToForehead + noseToChin;
      const pitch = pitchTotal > 1 ? (noseToChin - noseToForehead) / pitchTotal : 0;
      // positive = looking down, negative = looking up

      const awayFiring = Math.abs(yaw) > YAW_LIMIT || pitch < -PITCH_UP_LIMIT;
      console.log(`[proctoring] yaw=${yaw.toFixed(3)} pitch=${pitch.toFixed(3)} | away=${awayFiring}`);
      checkGrace("looking_away", GRACE_LOOKING_AWAY, awayFiring);

      // Iris gaze for looking_down (refineLandmarks gives landmarks 468–477).
      // Pitch < -PITCH_DOWN_LIMIT is used as a complement: when looking down at desk
      // the chin rises in 2D projection, pushing pitch more negative.
      const leftIris  = kp[LEFT_IRIS_CENTER];
      const rightIris = kp[RIGHT_IRIS_CENTER];
      if (leftIris && rightIris) {
        const leftTop    = kp[159];
        const leftBottom = kp[145];
        if (leftTop && leftBottom) {
          const eyeH = leftBottom.y - leftTop.y;
          if (eyeH > 2) {
            const ratio = (leftIris.y - leftTop.y) / eyeH;
            const downFiring = ratio > IRIS_DOWN_RATIO || pitch < -PITCH_DOWN_LIMIT;
            checkGrace("looking_down", GRACE_LOOKING_DOWN, downFiring);
          } else {
            const downFiring = pitch < -PITCH_DOWN_LIMIT;
            console.log(`[proctoring] eyeH=${eyeH.toFixed(1)} (small) pitch fallback | down=${downFiring}`);
            checkGrace("looking_down", GRACE_LOOKING_DOWN, downFiring);
          }
        }
      } else {
        const downFiring = pitch < -PITCH_DOWN_LIMIT;
        console.log(`[proctoring] no iris | pitch fallback pitch=${pitch.toFixed(3)} | down=${downFiring}`);
        checkGrace("looking_down", GRACE_LOOKING_DOWN, downFiring);
      }
    }
  }, [videoRef, checkGrace, logEvent]);

  // Start / stop detection loop
  useEffect(() => {
    if (!enabled) {
      clearInterval(timerRef.current);
      return;
    }
    loadDetector().then(() => {
      if (!mountedRef.current) return;
      clearInterval(timerRef.current); // clear any stale interval before starting fresh
      timerRef.current = setInterval(runDetection, DETECT_INTERVAL_MS);
    });
    return () => clearInterval(timerRef.current);
  }, [enabled, loadDetector, runDetection]);

  const resetEvents = useCallback(() => {
    const copy = [...eventsRef.current];
    eventsRef.current = [];
    setCheatCount(0);
    gracesRef.current  = {};
    cooldownRef.current = {};
    return copy;
  }, []);

  return { cheatCount, resetEvents, faceDetected };
}
